import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function InquiryModal() {
  const { inquiryProduct, setInquiryProduct } = useCart();
  const [form, setForm] = useState({ name: "", company: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (inquiryProduct) {
      setForm((f) => ({
        ...f,
        message: `Vorrei maggiori informazioni su ${inquiryProduct.name} (${inquiryProduct.sku}): disponibilità, taglie e tempi di consegna.`,
      }));
    }
  }, [inquiryProduct]);

  const close = () => setInquiryProduct(null);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error("Compila nome, email e messaggio.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, product_name: inquiryProduct?.name || "" });
      toast.success("Richiesta inviata. Ti ricontatteremo entro 24h.");
      setForm({ name: "", company: "", email: "", message: "" });
      close();
    } catch (e) {
      toast.error("Errore durante l'invio. Riprova.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {inquiryProduct && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[85] bg-ink/50 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            className="fixed left-1/2 top-1/2 z-[86] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 bg-white p-8 shadow-2xl"
            data-testid="inquiry-modal"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-terracotta">Richiesta rapida</p>
                <h3 className="mt-2 font-display text-2xl font-bold tracking-tight text-ink" data-testid="inquiry-product-name">
                  {inquiryProduct.name}
                </h3>
                <p className="font-mono text-[11px] text-ink/45">{inquiryProduct.sku} · MOQ {inquiryProduct.moq} paia</p>
              </div>
              <button
                onClick={close}
                className="flex h-9 w-9 items-center justify-center border border-black/10 text-ink transition-colors duration-300 hover:bg-ink hover:text-white"
                aria-label="Chiudi richiesta"
                data-testid="inquiry-close-button"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-6 space-y-3">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nome e cognome *"
                className="h-11 w-full border border-black/15 bg-white px-4 text-sm text-ink placeholder:text-ink/35 focus:border-ink focus:outline-none"
                data-testid="inquiry-input-name"
              />
              <input
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                placeholder="Azienda"
                className="h-11 w-full border border-black/15 bg-white px-4 text-sm text-ink placeholder:text-ink/35 focus:border-ink focus:outline-none"
                data-testid="inquiry-input-company"
              />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email aziendale *"
                className="h-11 w-full border border-black/15 bg-white px-4 text-sm text-ink placeholder:text-ink/35 focus:border-ink focus:outline-none"
                data-testid="inquiry-input-email"
              />
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                className="w-full border border-black/15 bg-white px-4 py-3 text-sm text-ink focus:border-ink focus:outline-none"
                data-testid="inquiry-input-message"
              />
              <button
                onClick={submit}
                disabled={sending}
                className="w-full bg-ink px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-cobalt disabled:opacity-50"
                data-testid="inquiry-submit-button"
              >
                {sending ? "Invio in corso…" : "Invia richiesta"}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
